import React from 'react';
import { Archive } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { authQueries } from 'src/api/queries';
import { Button } from 'src/components/ui/button/button';
import { useModalLoader } from 'src/components/ModalLoader/Provider';
import { useToast } from 'src/components/ui/use-toast';
import { requestClient } from 'src/lib/services/api/request-api';
import { useRequestsCounts } from 'src/models';
import { useUpsell } from 'src/features/upsell-dialog/use-upsell';
import { useOpenRequestModal } from 'src/features/requests/request-form/use-open-request-modal';
import { formatUtcDate } from 'src/features/requests/request-table/request-status-cell';

/**
 * File Retention (#2752): a request the client deleted sits in Archived,
 * read-only. It says who deleted it and when, that it can't be reopened, and
 * offers the way onward: a copy as a new draft, opened in the request form.
 */
const RequestDeletedByClientBanner: React.FC<{
  request: {
    id: string;
    title?: string | null;
    archived_at?: string | null;
    archived_by_name?: string | null;
  };
}> = ({ request }) => {
  const { t, i18n } = useTranslation();
  const { loading } = useModalLoader();
  const { add } = useRequestsCounts();
  const { checkRequestsPrivileges } = useUpsell();
  const { openRequestModal } = useOpenRequestModal();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const on = request.archived_at ? formatUtcDate(request.archived_at, i18n.language) : '';
  const deletedBy = request.archived_by_name
    ? t('request_deleted_by_on', {
        name: request.archived_by_name,
        date: on,
        defaultValue: 'Deleted by {{name}} on {{date}}.',
      })
    : t('request_deleted_on', { date: on, defaultValue: 'Deleted on {{date}}.' });

  const duplicate = () =>
    checkRequestsPrivileges(async () => {
      let copyId: string | undefined;
      loading(true);
      try {
        copyId = (await requestClient.clone(request.id)).id;
        add(copyId, true);
        // The Draft tab count and the list catch up now, not on the next poll.
        queryClient.invalidateQueries({ queryKey: [authQueries.requestCounts] });
        queryClient.invalidateQueries({ queryKey: [authQueries.requests] });
        toast({
          description: t('request_duplicated_as_draft', {
            title: request.title || t('this request', { defaultValue: 'this request' }),
            defaultValue: 'A copy of {{title}} was created as a new draft.',
          }),
        });
      } catch (e) {
        /* The API client shows the error toast. */
      } finally {
        loading(false);
      }

      // After the loader is gone: opening may ask about an unsaved draft first.
      if (copyId) {
        await openRequestModal(copyId);
      }
    });

  return (
    <div
      className={
        'tw-mb-3 tw-flex tw-flex-col tw-gap-3 tw-rounded-md tw-bg-neutral-100 tw-p-3 tw-text-sm tw-text-text-subtle sm:tw-flex-row sm:tw-items-center sm:tw-justify-between'
      }
      role={'status'}
    >
      <div className={'tw-flex tw-items-start tw-gap-2'}>
        <Archive size={16} className={'tw-mt-0.5 tw-shrink-0'} aria-hidden={'true'} />
        <span>
          {deletedBy}{' '}
          {t('request_deleted_cannot_reopen', {
            defaultValue: "It can't be reopened. To submit it again, duplicate it as a new request.",
          })}
        </span>
      </div>
      <Button type={'button'} size={'sm'} variant={'outline'} className={'tw-shrink-0'} onClick={duplicate}>
        {t('duplicate_and_submit_new_request', { defaultValue: 'Duplicate & Submit New Request' })}
      </Button>
    </div>
  );
};

export { RequestDeletedByClientBanner };
